import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { ImageUploader } from "../ImageUploader";
import { Plus, Trash2, Megaphone, Images, LayoutGrid } from "lucide-react";
import { toast } from "sonner";

export default function AdminContent() {
  const [announcement, setAnnouncement] = useState("");
  const [slides, setSlides] = useState([]);
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then(({ data }) => {
      setAnnouncement(data.announcement || "");
      setSlides(data.hero_slides || []);
      setCategories(data.categories || []);
    });
  }, []);

  const updateSlide = (i, field, value) => setSlides((s) => s.map((sl, idx) => (idx === i ? { ...sl, [field]: value } : sl)));
  const addSlide = () => setSlides((s) => [...s, { img: "", eyebrow: "", title: "", subtitle: "", cta: "Shop Now", link: "/shop", align: "left" }]);
  const removeSlide = (i) => setSlides((s) => s.filter((_, idx) => idx !== i));

  const updateCat = (i, field, value) => setCategories((c) => c.map((ct, idx) => (idx === i ? { ...ct, [field]: value } : ct)));
  const addCat = () => setCategories((c) => [...c, { key: "", label: "" }]);
  const removeCat = (i) => setCategories((c) => c.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", {
        announcement,
        hero_slides: slides,
        categories: categories.filter((c) => c.key && c.label),
      });
      toast.success("Homepage content saved — it's live now");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-content-manager">
      <div className="rounded-2xl border bg-white dark:bg-slate-900 p-5">
        <h3 className="font-display font-bold flex items-center gap-2 mb-3"><Megaphone size={18} className="text-[#E63946]" /> Announcement bar</h3>
        <Input value={announcement} onChange={(e) => setAnnouncement(e.target.value)} placeholder="Top banner message" data-testid="content-announcement" />
      </div>

      <div className="rounded-2xl border bg-white dark:bg-slate-900 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold flex items-center gap-2"><Images size={18} className="text-[#E63946]" /> Hero banners</h3>
          <Button variant="outline" size="sm" onClick={addSlide} data-testid="content-add-slide"><Plus size={14} className="mr-1" /> Add banner</Button>
        </div>
        <div className="space-y-5">
          {slides.map((s, i) => (
            <div key={i} className="rounded-xl border p-4" data-testid={`content-slide-${i}`}>
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-semibold">Banner {i + 1}</span>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => removeSlide(i)} data-testid={`content-remove-slide-${i}`}><Trash2 size={14} className="text-red-500" /></Button>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Banner image</Label>
                  <ImageUploader images={s.img ? [s.img] : []} onChange={(imgs) => updateSlide(i, "img", imgs[imgs.length - 1] || "")} />
                </div>
                <div className="space-y-2">
                  <div><Label className="text-xs">Eyebrow</Label><Input value={s.eyebrow || ""} onChange={(e) => updateSlide(i, "eyebrow", e.target.value)} /></div>
                  <div><Label className="text-xs">Title</Label><Input value={s.title || ""} onChange={(e) => updateSlide(i, "title", e.target.value)} /></div>
                  <div><Label className="text-xs">Subtitle</Label><Input value={s.subtitle || ""} onChange={(e) => updateSlide(i, "subtitle", e.target.value)} /></div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3 mt-3">
                <div><Label className="text-xs">Button text</Label><Input value={s.cta || ""} onChange={(e) => updateSlide(i, "cta", e.target.value)} /></div>
                <div><Label className="text-xs">Button link</Label><Input value={s.link || ""} onChange={(e) => updateSlide(i, "link", e.target.value)} placeholder="/shop?gender=Men" /></div>
                <div>
                  <Label className="text-xs">Button align</Label>
                  <select value={s.align || "left"} onChange={(e) => updateSlide(i, "align", e.target.value)} className="w-full h-10 rounded-md border bg-transparent px-2 text-sm">
                    <option value="left">Left</option>
                    <option value="right">Right</option>
                  </select>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border bg-white dark:bg-slate-900 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold flex items-center gap-2"><LayoutGrid size={18} className="text-[#E63946]" /> Category tiles & menu</h3>
          <Button variant="outline" size="sm" onClick={addCat} data-testid="content-add-category"><Plus size={14} className="mr-1" /> Add category</Button>
        </div>
        <div className="space-y-2">
          {categories.map((c, i) => (
            <div key={i} className="flex gap-2 items-center" data-testid={`content-category-${i}`}>
              <Input value={c.key} onChange={(e) => updateCat(i, "key", e.target.value)} placeholder="Key (e.g. T-Shirts)" />
              <Input value={c.label} onChange={(e) => updateCat(i, "label", e.target.value)} placeholder="Label shown" />
              <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={() => removeCat(i)}><Trash2 size={14} className="text-red-500" /></Button>
            </div>
          ))}
        </div>
      </div>

      <Button onClick={save} disabled={saving} className="bg-[#E63946] hover:bg-[#d12836]" data-testid="content-save">
        {saving ? "Saving..." : "Save homepage content"}
      </Button>
    </div>
  );
}
